import { useTranslation } from "react-i18next"
import { useEffect, useState } from "react"
import { Dialog } from "primereact/dialog"
import PPUtils from "../../utils/PPUtils"
import Select from "react-select"
import RuleEngineBackend from "../services/RuleEngineBackend"
import { useNavigate, useParams } from "react-router-dom"
import { toast } from "react-toastify"
import PPDecimalInput from "../common/PPDecimalInput";
import { resolveChannelId, selectChannel } from "../common/priceManagementChannelStorage"
import {
    getDefaultProviderFilterFormState,
    parseProviderFilterData,
    serializeProviderFilterData,
} from "../common/providerFilterUtils"
import PPDeleteConfirmationModal from "../editor/modals/PPDeleteConfirmationModal"
import PPErrorBoundary from "../../components/layout/PPErrorBoundary"
import PPMatchingStatusBanner from "../../components/matching/PPMatchingStatusBanner"
import "../../styles/priceman.css"

const STANDARD_BACKUP_RULE_ID = 1

const SETTINGS_TABS = [
    { id: "rule-settings", label: "Regeleinstellungen" },
    { id: "channels", label: "Kanäle" },
    { id: "backup-rules", label: "Backup-Regeln" },
    { id: "provider-filter", label: "Anbieter-Filter" },
    { id: "monitoring", label: "Sicherheitsmonitoring" }
]

const DEFAULT_SETTINGS_TAB = "rule-settings"

const isStandardBackupRule = (rule) => {
    if (!rule) {
        return false;
    }
    return Number(rule.id) === STANDARD_BACKUP_RULE_ID;
};

const canDeleteBackupRule = (rule, customerId) => {
    if (!rule || isStandardBackupRule(rule)) {
        return false;
    }
    return Number(rule.customerId) === Number(customerId);
};

export default function PPPriceManagementGlobalRules() {
    const GlobalConstants = {
        DEFAULT_MAX_DECREASE_ADJUSTMENT: {
            id: -1,
            key: "max_decrease_adjustment",
            defaultValue: 10,
            value: null,
            changed: false
        },
        DEFAULT_MAX_INCREASE_ADJUSTMENT: {
            id: -1,
            key: "max_increase_adjustment",
            defaultValue: 10,
            value: null,
            changed: false
        },
        DEFAULT_EK_PLUS_EURO: {
            id: -1,
            key: "ek_plus_euro",
            defaultValue: 0,
            value: null,
            changed: false
        },
        DEFAULT_EK_PLUS_PERCENT: {
            id: -1,
            key: "ek_plus_percent",
            defaultValue: 10,
            value: null,
            changed: false
        },
        DEFAULT_EK_PLUS_EURO2: {
            id: -1,
            key: "ek_plus_euro2",
            defaultValue: 0,
            value: null,
            changed: false
        },
        DEFAULT_MAX_FOREIGN_SHOP_PRODUCT_AGE: {
            id: -1,
            key: "max_foreign_shop_product_age",
            defaultValue: 48,
            value: null,
            changed: false
        },
        DEFAULT_FREE_DELIVERY_THRESHOLD: {
            id: -1,
            key: "free_delivery_threshold",
            defaultValue: null,
            value: null,
            changed: false
        },
        DEFAULT_DELIVERY_COSTS: {
            id: -1,
            key: "deliveryCosts",
            defaultValue: null,
            value: null,
            changed: false
        },
        PASS_PRODUCTS_TO_NEXT_RULE_ACTIVATED: {
            id: -1,
            key: "pass_products_to_next_rule_activated",
            defaultValue: false,
            value: null,
            changed: false
        },
        PASS_PRODUCTS_TO_NEXT_RULE_VALUE: {
            id: -1,
            key: "pass_products_to_next_rule_value",
            defaultValue: null,
            value: null,
            changed: false
        },
        USE_CHANNEL_PRICE_AS_BASE_PRICE: {
            id: -1,
            key: "use_channel_price_as_base_price",
            defaultValue: true,
            value: null,
            changed: false
        },
        SECURITY_MONITORING_ACTIVATE: {
            id: -1,
            key: "sec_monitoring_activate",
            defaultValue: false,
            value: null,
            changed: false
        },
        SECURITY_MONITORING_THRESHOLD_BELOW_MARGE: {
            id: -1,
            key: "sec_monitoring_threshold_below_marge",
            defaultValue: -1,
            value: null,
            changed: false
        },
        SECURITY_MONITORING_BELOW_MARGE_PERCENTAGE: {
            id: -1,
            key: "sec_monitoring_below_marge_percentage",
            defaultValue: -1,
            value: null,
            changed: false
        },
        SECURITY_MONITORING_THRESHOLD_PRODUCTS_PRICE_REDUCED_COUNT: {
            id: -1,
            key: "sec_monitoring_threshold_products_down",
            defaultValue: -1,
            value: null,
            changed: false
        },
        SECURITY_MONITORING_THRESHOLD_COUNT_PRICE_CHANGES: {
            id: -1,
            key: "se_monitoring_threshold_price_changes",
            defaultValue: -1,
            value: null,
            changed: false
        }
    }

    const [ruleState, setRuleState] = useState(GlobalConstants)
    const [channels, setChannels] = useState([])
    const [providerFilters, setProviderFilters] = useState([])
    const [backupRules, setBackupRules] = useState([])
    const [backupRuleDeleteModalVisible, setBackupRuleDeleteModalVisible] = useState(false)
    const [backupRulePendingDelete, setBackupRulePendingDelete] = useState(null)

    const [selectedChannelID, setSelectedChannelID] = useState(null)

    const [addChannelName, setAddChannelName] = useState("")
    const [securityEmails, setSecurityEmails] = useState("")

    const [producerFilterModalOpen, setProducerFilterModalOpen] = useState(false)
    const [producerFilterName, setProducerFilterName] = useState("")
    const [blacklistWhitelistState, setBlacklistWhitelistState] = useState(true)
    const [providerListText, setProviderListText] = useState("")
    const [stockState, setStockState] = useState({
        green: true,
        yellow: true,
        red: true,
    })
    const [minRating, setMinRating] = useState(0)

    const resetProviderFilterForm = () => {
        const defaults = getDefaultProviderFilterFormState()
        setProducerFilterName("")
        setBlacklistWhitelistState(defaults.blacklistWhitelistState)
        setProviderListText(defaults.providerListText)
        setStockState(defaults.stockState)
        setMinRating(defaults.minRating)
    }

    const applyProviderFilterForm = (filterData) => {
        const parsed = parseProviderFilterData(filterData)
        setBlacklistWhitelistState(parsed.blacklistWhitelistState)
        setProviderListText(parsed.providerListText)
        setStockState(parsed.stockState)
        setMinRating(parsed.minRating)
    }

    const [constantsLoading, setConstantsLoading] = useState(true);
    const [editingProviderFilter, setEditingProviderFilter] = useState(null);

    const [t] = useTranslation()
    const navigate = useNavigate()
    const { section } = useParams()
    const selectedSection = SETTINGS_TABS.some((tab) => tab.id === section) ? section : DEFAULT_SETTINGS_TAB

    useEffect(() => {
        if (section !== selectedSection) {
            navigate(`/pricemanagement/globalRules/${selectedSection}`, { replace: true })
        }
    }, [section, selectedSection, navigate])

    const selectSection = (tabId) => {
        if (tabId !== selectedSection) {
            navigate(`/pricemanagement/globalRules/${tabId}`)
        }
    }

    let auth = PPUtils.getLogin()
    const userId = auth.user?.id
    const token = auth.token
    const customerId = PPUtils.getEffectiveCustomerId(auth)

    useEffect(() => {
        async function fetchData() {
            if (customerId == null) {
                return
            }
            let channelIdToUse = selectedChannelID
            const allChannels = await RuleEngineBackend.getChannels(customerId, token)
            if (allChannels) {
                const channelList = Array.isArray(allChannels) ? allChannels : [allChannels]
                setChannels(channelList)
                channelIdToUse = resolveChannelId(channelList, customerId, selectedChannelID)
                if (channelIdToUse !== selectedChannelID) {
                    setSelectedChannelID(channelIdToUse)
                }
            }

            if (channelIdToUse == null) {
                setConstantsLoading(false)
            } else if (channelIdToUse === selectedChannelID) {
                setConstantsLoading(true);
                const entries = Object.entries(ruleState);
                const fetchedList = await Promise.all(
                    entries.map(([stateKey]) =>
                        RuleEngineBackend.getRuleConstant(token, customerId, null, channelIdToUse, stateKey)
                    )
                );
                const fetchedConstants = {};
                entries.forEach(([stateKey], i) => {
                    fetchedConstants[stateKey] = fetchedList[i];
                });
                updateEntireRulesState(fetchedConstants);
                setConstantsLoading(false);
            }

            const fetchedProviderFilters = await RuleEngineBackend.getProviderFilters(token, customerId);
            if (Array.isArray(fetchedProviderFilters)) {
                setProviderFilters(fetchedProviderFilters);
            }

            const fetchedBackupRules = await RuleEngineBackend.getBackupRules(token, customerId);
            if (Array.isArray(fetchedBackupRules)) {
                setBackupRules(fetchedBackupRules);
            }

            const emails = await RuleEngineBackend.getSecurityMonitoringEmails(token);
            if (emails && Array.isArray(emails)) {
                setSecurityEmails(emails.join(", "));
            } else if (typeof emails === 'string') {
                setSecurityEmails(emails);
            }
        }
        fetchData()
    }, [customerId, userId, token, t, selectedChannelID])

    const saveGlobalRules = async () => {
        const selectedChannel = channels.find(ch => ch.id === selectedChannelID);
        const response = await RuleEngineBackend.updateRuleConstants(
            token,
            ruleState,
            selectedChannelID,
            customerId,
            selectedChannel
        );

        const emailResponse = await RuleEngineBackend.setSecurityMonitoringEmails(token, securityEmails);

        const constantsOk = response?.ok;
        const emailsOk = emailResponse?.ok;

        if (constantsOk && emailsOk) {
            toast.success("Regeln gespeichert");

            setRuleState((prev) => {
                const updated = { ...prev };
                response.results.forEach(result => {
                    if (result.success && result.data?.id) {
                        updated[result.key].id = result.data.id;
                    }
                    updated[result.key].changed = false;
                });
                return updated;
            });
        } else if (constantsOk && !emailsOk) {
            toast.warning("Regeln gespeichert, aber E-Mails konnten nicht gespeichert werden");
        } else {
            toast.error("Regeln konnten nicht gespeichert werden");
        }
    }

    const updateEntireRulesState = (newRules) => {
        setRuleState((prev) => {
            const updated = { ...prev };
            for (const [key] of Object.entries(prev)) {
                if (newRules[key]) {
                    updated[key].id = newRules[key].id;
                    updated[key].value = newRules[key].value;
                    updated[key].changed = false;
                }
            }
            return updated;
        });
    }

    const updateRuleState = (key, value) => {
        setRuleState((prev) => {
            const updated = { ...prev };
            updated[key] = {
                ...updated[key],
                value: value,
                changed: true
            };
            return updated;
        });
    }

    const getValueByKey = (key) => {
        const rule = ruleState[key]
        if (rule.changed) return rule.value;
        if (rule.value !== null && rule.value !== undefined) {
            if (typeof rule.defaultValue === 'boolean') return rule.value !== 0;
            return rule.value;
        }
        return rule.defaultValue;
    }

    const confirmDeleteBackupRule = async () => {
        if (!backupRulePendingDelete) {
            return;
        }

        if (!canDeleteBackupRule(backupRulePendingDelete, customerId)) {
            toast.error("Die Standard-Backup-Regel kann nicht gelöscht werden.");
            setBackupRuleDeleteModalVisible(false);
            setBackupRulePendingDelete(null);
            return;
        }

        const response = await RuleEngineBackend.deleteRule(userId, token, backupRulePendingDelete.id);
        if (response?.ok) {
            setBackupRules((prev) => prev.filter((entry) => entry.id !== backupRulePendingDelete.id));
            toast.success("Backup-Regel gelöscht");
        } else {
            toast.error("Backup-Regel konnte nicht gelöscht werden");
        }

        setBackupRuleDeleteModalVisible(false);
        setBackupRulePendingDelete(null);
    };

    if (customerId == null) {
        return (
            <div className="container-fluid priceman-page">
                <div className="card shadow">
                    <div className="card-body">
                        <PPMatchingStatusBanner variant="no_customer" />
                    </div>
                </div>
            </div>
        )
    }

    return (
        <>
            <div className="card shadow card-primary card-outline card-outline-tabs mb-3">
                <div className="card-header p-0 border-bottom-0">
                    <ul className="nav nav-tabs" id="custom-tabs-four-tab" role="tablist">
                        {SETTINGS_TABS.map((tab) => {
                            const isActive = selectedSection === tab.id
                            return (
                                <li className="nav-item" key={tab.id}>
                                    <a
                                        className={`nav-link${isActive ? " active" : ""}`}
                                        href={`/pricemanagement/globalRules/${tab.id}`}
                                        role="tab"
                                        aria-selected={isActive}
                                        onClick={(event) => {
                                            event.preventDefault()
                                            selectSection(tab.id)
                                        }}>
                                        {tab.label}
                                    </a>
                                </li>
                            )
                        })}
                    </ul>
                </div>
                <div className="card-body position-relative">
                    {constantsLoading && (
                        <div
                            className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                            style={{ backgroundColor: "rgba(255,255,255,0.75)", zIndex: 10 }}
                        >
                            <div className="text-center">
                                <output className="spinner-border text-primary"  />
                                <div className="mt-2 text-muted">Einstellungen werden geladen…</div>
                            </div>
                        </div>
                    )}
                    <div className="tab-content">
                        <div className="p-3 pp-form-stack-sm priceman-page-body">

                            <div className="d-flex row justify-content-end">
                                <button className="btn btn-success btn-lg col-12 col-md-2 mt-2 mt-md-0" onClick={() => saveGlobalRules()}>
                                    <i className="fas fa-save me-2" />{' '}Speichern
                                </button>
                            </div>

                            <PPErrorBoundary variant="section" resetKeys={[selectedSection]}>
                            {selectedSection === "rule-settings" && <>
                                <div className="w-100 row">
                                    <Select
                                        className="priceman-channel-select mt-2 mt-md-0"
                                        placeholder="Kanal"
                                        onChange={(event) => selectChannel(customerId, event?.id ?? null, setSelectedChannelID)}
                                        value={channels
                                            .map((channel) => ({ label: channel.channelName, id: channel.id, value: channel.id }))
                                            .find((option) => option.id === selectedChannelID) || null}
                                        options={channels.map((channel) => ({ label: channel.channelName, id: channel.id, value: channel.id }))}
                                    />
                                    <div className="d-flex align-items-center col-12 col-md-6 mt-2 mt-md-0 p-0">
                                        <i
                                            className="fas fa-question-circle ms-2"
                                            title="Konstanten gelten kanalbezogen und koennen innerhalb einzelner Regeln ueberschrieben werden."
                                        />
                                        <p className="mb-0 ms-2">Konstanten können innerhalb einer Regel überschrieben werden</p>
                                    </div>
                                </div>

                                <h6 className="fw-semibold mb-3">Maximale Preisangleichung</h6>

                                <table className="table table-bordered col-12 col-md-6 pp-form-table">
                                    <thead>
                                        <tr>
                                            <th scope="col">Einstellung</th>
                                            <th scope="col">Wert</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr className="w-100">
                                            <td className="p-2 col-6">
                                                <div className="pp-form-cell-label">
                                                    <p className="m-0">Maximale Preisminderung (in %):</p>
                                                </div>
                                            </td>
                                            <td className="p-2">
                                                <div className="input-group input-group-sm">
                                                    <PPDecimalInput
                                                        value={getValueByKey("DEFAULT_MAX_DECREASE_ADJUSTMENT")}
                                                        onValueChange={(v) => updateRuleState("DEFAULT_MAX_DECREASE_ADJUSTMENT", v)}
                                                        className="form-control input-sm"
                                                        decimalsLimit={2}
                                                        suffix="%"
                                                        placeholder="3"
                                                    />
                                                </div>
                                            </td>
                                        </tr>

                                        <tr>
                                            <td className="p-2">
                                                <div className="pp-form-cell-label">
                                                    <p className="m-0">Maximale Preissteigerung (in %):</p>
                                                </div>
                                            </td>
                                            <td className="p-2">
                                                <div className="input-group input-group-sm">
                                                    <PPDecimalInput
                                                        value={getValueByKey("DEFAULT_MAX_INCREASE_ADJUSTMENT")}
                                                        onValueChange={(v) => updateRuleState("DEFAULT_MAX_INCREASE_ADJUSTMENT", v)}
                                                        className="form-control input-sm"
                                                        decimalsLimit={2}
                                                        suffix="%"
                                                        placeholder="3"
                                                    />
                                                </div>
                                            </td>
                                        </tr>

                                        <tr>
                                            <td className="p-2">
                                                <div className="pp-form-cell-label">
                                                    <p className="m-0">Mindestpreis:</p>
                                                </div>
                                            </td>
                                            <td className="p-2">
                                                <div className="input-group input-group-sm d-flex align-items-center">

                                                    <span className="me-2 text-nowrap">{"( EK +"}</span>

                                                    <PPDecimalInput
                                                        value={getValueByKey("DEFAULT_EK_PLUS_EURO")}
                                                        onValueChange={(v) => updateRuleState("DEFAULT_EK_PLUS_EURO", v)}
                                                        className={`form-control`}
                                                        decimalsLimit={2}
                                                        suffix={"€"}
                                                        step={1}
                                                        maxLength={6}
                                                    />

                                                    <span className="mx-2 text-nowrap">{" + "}</span>


                                                    <PPDecimalInput
                                                        value={getValueByKey("DEFAULT_EK_PLUS_PERCENT")}
                                                        onValueChange={(v) => updateRuleState("DEFAULT_EK_PLUS_PERCENT", v)}
                                                        className={`form-control`}
                                                        decimalsLimit={2}
                                                        suffix={"%"}
                                                        step={1}
                                                        maxLength={3}
                                                    />

                                                    <span className="mx-2 text-nowrap">{" + "}</span>

                                                    <PPDecimalInput
                                                        value={getValueByKey("DEFAULT_EK_PLUS_EURO2")}
                                                        onValueChange={(v) => updateRuleState("DEFAULT_EK_PLUS_EURO2", v)}
                                                        className={`form-control`}
                                                        decimalsLimit={2}
                                                        suffix={"€"}
                                                        step={1}
                                                        maxLength={6}
                                                    />
                                                </div>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>

                                <h6 className="fw-semibold mb-3">Crawl-Datum</h6>

                                <table className="table table-bordered col-12 col-md-6 pp-form-table">
                                    <thead>
                                        <tr>
                                            <th scope="col">Einstellung</th>
                                            <th scope="col">Wert</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr className="w-100">
                                            <td className="p-2 col-6">
                                                <div className="pp-form-cell-label">
                                                    <p className="m-0">Fremdshop-Preise dürfen nicht älter sein als</p>
                                                </div>
                                            </td>
                                            <td className="p-2">
                                                <div className="input-group input-group-sm d-flex align-items-center">
                                                    <input
                                                        value={getValueByKey("DEFAULT_MAX_FOREIGN_SHOP_PRODUCT_AGE")}
                                                        onChange={(e) =>
                                                            updateRuleState(
                                                                "DEFAULT_MAX_FOREIGN_SHOP_PRODUCT_AGE",
                                                                e.target.value
                                                            )
                                                        }
                                                        className="form-control input-sm"
                                                        type="number"
                                                        id="inputmax_price_adj"
                                                        placeholder="3"
                                                    />
                                                    <span className="mx-2 text-nowrap"> Stunden</span>
                                                </div>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>

                                <h6 className="fw-semibold mb-3">Produkte an weitere Regeln durchreichen</h6>
                                <p className="text-muted small">Nur Aktivierung und Mindestalter für das Durchreichen an die nächste Regel.</p>

                                <table className="table table-bordered col-12 col-md-6 pp-form-table">
                                    <tbody>
                                        <tr className="w-100">
                                            <td className="p-2 col-6">
                                                <div className="pp-form-cell-label">
                                                    <p className="m-0">aktiviert</p>
                                                </div>
                                            </td>
                                            <td className="p-2">
                                                <div className="d-flex align-items-center justify-content-center">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        onChange={() =>
                                                            updateRuleState(
                                                                "PASS_PRODUCTS_TO_NEXT_RULE_ACTIVATED",
                                                                !getValueByKey("PASS_PRODUCTS_TO_NEXT_RULE_ACTIVATED")
                                                            )
                                                        }
                                                        checked={getValueByKey("PASS_PRODUCTS_TO_NEXT_RULE_ACTIVATED")}
                                                        id="pass_rules"
                                                    />
                                                </div>
                                            </td>
                                        </tr>
                                        {getValueByKey("PASS_PRODUCTS_TO_NEXT_RULE_ACTIVATED") && (
                                            <tr>
                                                <td className="p-2">
                                                    <div className="pp-form-cell-label">
                                                        <p className="m-0">Fremdshop-Produkte-Alter</p>
                                                    </div>
                                                </td>
                                                <td className="p-2">
                                                    <div className="input-group input-group-sm d-flex align-items-center">
                                                        <input
                                                            value={getValueByKey("PASS_PRODUCTS_TO_NEXT_RULE_VALUE")}
                                                            onChange={(e) =>
                                                                updateRuleState(
                                                                    "PASS_PRODUCTS_TO_NEXT_RULE_VALUE",
                                                                    e.target.value
                                                                )
                                                            }
                                                            className="form-control input-sm"
                                                            type="number"
                                                            placeholder="3"
                                                        />
                                                        <span className="mx-2 text-nowrap"> Tage</span>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>

                                <h6 className="fw-semibold mb-3">Versandkosten</h6>
                                <table className="table table-bordered col-12 col-md-6 pp-form-table">
                                    <thead>
                                        <tr>
                                            <th scope="col">Einstellung</th>
                                            <th scope="col">Wert</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td className="p-2 col-6">
                                                <div className="pp-form-cell-label">
                                                    <p className="m-0">Versandkosten</p>
                                                </div>
                                            </td>
                                            <td className="p-2">
                                                <PPDecimalInput
                                                    value={getValueByKey("DEFAULT_DELIVERY_COSTS")}
                                                    onValueChange={(v) => updateRuleState("DEFAULT_DELIVERY_COSTS", v)}
                                                    className="form-control"
                                                    decimalsLimit={2}
                                                    suffix={"€"}
                                                />
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="p-2">
                                                <div className="pp-form-cell-label">
                                                    <p className="m-0">Versandkosten frei ab</p>
                                                </div>
                                            </td>
                                            <td className="p-2">
                                                <PPDecimalInput
                                                    value={getValueByKey("DEFAULT_FREE_DELIVERY_THRESHOLD")}
                                                    onValueChange={(v) => updateRuleState("DEFAULT_FREE_DELIVERY_THRESHOLD", v)}
                                                    className="form-control"
                                                    decimalsLimit={2}
                                                    suffix={"€"}
                                                />
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>

                                <h6 className="fw-semibold mb-3">Basispreis</h6>

                                <table className="table table-bordered col-12 col-md-6 pp-form-table">
                                    <thead>
                                        <tr>
                                            <th scope="col">Einstellung</th>
                                            <th scope="col">Wert</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr className="w-100">
                                            <td className="p-2 col-6">
                                                <div className="pp-form-cell-label">
                                                    <p className="m-0">Kanalpreis als Basispreis für die Preisberechnung <i
                                                        className="fas fa-question-circle ms-2"
                                                        title="Über diese Checkbox kann ausgewählt werden, welcher Produktpreis als Basispreis für die Preisberechnung herangezogen wird. 
                                                                Entweder der zuletzt berechnete Preis des jeweiligen Kanals, 
                                                                oder stets der im Produkt-Import des PreisPiranha-Systems angegebene Onlineshop-Preis."
                                                    /></p>
                                                </div>
                                            </td>
                                            <td className="p-2">
                                                <div className="d-flex align-items-center justify-content-center">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        onChange={() =>
                                                            updateRuleState(
                                                                "USE_CHANNEL_PRICE_AS_BASE_PRICE",
                                                                !getValueByKey("USE_CHANNEL_PRICE_AS_BASE_PRICE")
                                                            )
                                                        }
                                                        checked={getValueByKey("USE_CHANNEL_PRICE_AS_BASE_PRICE")}
                                                        id="pass_rules"
                                                    />
                                                </div>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </>}

                            {selectedSection === "channels" && (
                                <div className="d-flex flex-column w-100 mt-2">
                                    <div className="d-flex flex-column pp-form-stack-sm">
                                        <div className="w-100 row">
                                            <table className="table table-striped table-hover">
                                                <thead>
                                                    <tr>
                                                        <th scope="col">ID</th>
                                                        <th scope="col">Name</th>
                                                        <th scope="col">Aktion</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {channels.map((channel) => (
                                                        <tr key={`channels_gr_${channel.id}`}>
                                                            <td>{channel.id}</td>
                                                            <td>{channel.channelName}</td>
                                                            <td>
                                                                <button
                                                                    className={`btn btn-sm ${channel.hidden ? "btn-primary" : "btn-danger"}`}
                                                                    onClick={async () => {
                                                                        const response = channel.hidden
                                                                            ? await RuleEngineBackend.activateChannel(token, customerId, channel.id)
                                                                            : await RuleEngineBackend.deactivateChannel(token, customerId, channel.id);
                                                                        if (response?.ok) {
                                                                            setChannels((prev) => prev.map((entry) =>
                                                                                entry.id === channel.id
                                                                                    ? { ...entry, hidden: !channel.hidden }
                                                                                    : entry
                                                                            ));
                                                                            toast.success(channel.hidden ? "Kanal aktiviert" : "Kanal deaktiviert");
                                                                            return;
                                                                        }
                                                                        toast.error("Kanal konnte nicht aktualisiert werden");
                                                                    }}
                                                                >
                                                                    {channel.hidden ? "Aktivieren" : "Deaktivieren"}
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                        <div className="w-100 d-flex justify-content-between align-items-center flex-wrap">
                                            <input
                                                value={addChannelName}
                                                onChange={(e) => setAddChannelName(e.target.value)}
                                                type="text"
                                                id="channel_add_input"
                                                className="col-md-8 col-sm-7 form-control"
                                                placeholder="Kanalname"
                                            />
                                            <button
                                                className="btn btn-primary btn-lg col-md-3 col-sm-4 mt-2 mt-md-0"
                                                onClick={async () => {
                                                    const channelName = addChannelName.trim();
                                                    if (!channelName) {
                                                        toast.error("Bitte einen Kanalnamen eingeben");
                                                        return;
                                                    }
                                                    const createdChannel = await RuleEngineBackend.createChannel(token, customerId, channelName);
                                                    if (createdChannel?.id) {
                                                        setChannels((prev) => [...prev, createdChannel]);
                                                        selectChannel(customerId, createdChannel.id, setSelectedChannelID);
                                                        setAddChannelName("");
                                                        toast.success("Kanal hinzugefügt");
                                                        return;
                                                    }
                                                    toast.error("Kanal konnte nicht hinzugefügt werden");
                                                }}
                                            >
                                                Kanal hinzufügen
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {selectedSection === "backup-rules" && (
                                <div className="d-flex flex-column w-100 mt-2">
                                    <div className="d-flex flex-column pp-form-stack-sm">
                                        <div className="table-responsive">
                                            <table className="table table-striped table-hover">
                                                <thead>
                                                    <tr>
                                                        <th scope="col">ID</th>
                                                        <th scope="col">Aktiviert</th>
                                                        <th scope="col">Name</th>
                                                        <th scope="col">Beschreibung</th>
                                                        <th scope="col">Regel löschen</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {backupRules.length === 0 && (
                                                        <tr>
                                                            <td colSpan={5} className="text-muted">Keine Backup-Regeln vorhanden</td>
                                                        </tr>
                                                    )}
                                                    {backupRules.map((rule) => (
                                                        <tr
                                                            key={`backup_rule_${rule.id}`}
                                                            style={{ cursor: "pointer" }}
                                                            onClick={() => {
                                                                if (isStandardBackupRule(rule)) {
                                                                    toast.warning("Standard-Backup-Regel kann nicht bearbeitet werden");
                                                                    return;
                                                                }
                                                                navigate(`/pricemanagement/editor/${rule.id}`);
                                                            }}
                                                        >
                                                            <td>{rule.id}</td>
                                                            <td>{rule.enabled ? "Ja" : "Nein"}</td>
                                                            <td>{rule.name}</td>
                                                            <td>{rule.description || "-"}</td>
                                                            <td>
                                                                {canDeleteBackupRule(rule, customerId) ? (
                                                                    <button
                                                                        className="btn btn-sm btn-outline-danger"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setBackupRulePendingDelete(rule);
                                                                            setBackupRuleDeleteModalVisible(true);
                                                                        }}
                                                                    >
                                                                        Löschen
                                                                    </button>
                                                                ) : (
                                                                    <span className="text-muted">—</span>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                        <div className="w-100 d-flex justify-content-center justify-content-md-end mt-3">
                                            <button
                                                className="btn btn-primary btn-lg col-8 col-md-3"
                                                onClick={() => navigate("/pricemanagement/editor?createBackupRule=true&mode=backup")}>
                                                Backup Regel erstellen
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {selectedSection === "provider-filter" && (
                                <div className="d-flex flex-column w-100 mt-2">
                                    <div className="d-flex flex-column pp-form-stack-sm">
                                        <div className="table-responsive">
                                            <table className="table table-striped table-hover">
                                                <thead>
                                                    <tr>
                                                        <th scope="col">ID</th>
                                                        <th scope="col">Name</th>
                                                        <th scope="col">Filter löschen</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {providerFilters.length === 0 && (
                                                        <tr>
                                                            <td colSpan={3} className="text-muted">Keine Anbieter-Filter vorhanden</td>
                                                        </tr>
                                                    )}
                                                    {providerFilters.map((filter) => (
                                                        <tr
                                                            key={`provider_filter_${filter.id}`}
                                                            style={{ cursor: "pointer" }}
                                                            onClick={() => {
                                                                setEditingProviderFilter(filter);
                                                                setProducerFilterName(filter.name || "");
                                                                applyProviderFilterForm(filter.filterData);
                                                                setProducerFilterModalOpen(true);
                                                            }}>
                                                            <td>{filter.id}</td>
                                                            <td>{filter.name}</td>
                                                            <td>
                                                                <button
                                                                    className="btn btn-sm btn-outline-danger"
                                                                    onClick={async (e) => {
                                                                        e.stopPropagation();
                                                                        const response = await RuleEngineBackend.deleteProviderFilter(token, filter.id);
                                                                        if (response?.ok) {
                                                                            setProviderFilters((prev) => prev.filter((entry) => entry.id !== filter.id));
                                                                            toast.success("Anbieter-Filter gelöscht");
                                                                            return;
                                                                        }
                                                                        toast.error("Anbieter-Filter konnte nicht gelöscht werden");
                                                                    }}
                                                                >
                                                                    Löschen
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                        <div className="w-100 d-flex justify-content-center justify-content-md-end mt-3">
                                            <button
                                                onClick={() => {
                                                    setEditingProviderFilter(null);
                                                    resetProviderFilterForm();
                                                    setProducerFilterModalOpen(true);
                                                }}
                                                className="btn btn-primary btn-lg col-8 col-md-3">
                                                Anbieter Filter erstellen
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {selectedSection === "monitoring" && (
                                <table className="table table-bordered col-12 col-md-6 pp-form-table">
                                    <thead>
                                        <tr>
                                            <th scope="col">Einstellung</th>
                                            <th scope="col">Wert</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr className="w-100">
                                            <td className="p-2 col-6">
                                                <div className="pp-form-cell-label">
                                                    <p className="m-0">Sicherheitsmonitoring aktivieren</p>
                                                </div>
                                            </td>
                                            <td className="p-2">
                                                <div className="d-flex align-items-center justify-content-center">
                                                    <input
                                                        checked={getValueByKey("SECURITY_MONITORING_ACTIVATE")}
                                                        onChange={() =>
                                                            updateRuleState(
                                                                "SECURITY_MONITORING_ACTIVATE",
                                                                !ruleState["SECURITY_MONITORING_ACTIVATE"].value
                                                            )
                                                        }
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        id="inputmax_price_adj"
                                                        placeholder="3"
                                                    />
                                                </div>
                                            </td>
                                        </tr>

                                        <tr className="w-100">
                                            <td className="p-2 col-6">
                                                <div className="pp-form-cell-label">
                                                    <p className="m-0">E-Mails (Komma als Trennzeichen, gilt Kanalübergreifend)</p>
                                                </div>
                                            </td>
                                            <td className="p-2">
                                                <div className="input-group input-group-sm d-flex align-items-center">
                                                    <input
                                                        value={securityEmails}
                                                        onChange={(e) => setSecurityEmails(e.target.value)}
                                                        className="form-control input-sm"
                                                        type="text"
                                                        id="gl_emails"
                                                        placeholder="max@musterman.de, mia@musterfrau.de"
                                                    />
                                                </div>
                                            </td>
                                        </tr>

                                        <tr className="w-100">
                                            <td className="p-2 col-6">
                                                <div className="pp-form-cell-label">
                                                    <p className="m-0">Anzahl Preisänderungen pro Stunde unter Marge X%</p>
                                                </div>
                                            </td>
                                            <td className="p-2">
                                                <div className="input-group input-group-sm d-flex align-items-center">
                                                    <PPDecimalInput
                                                        value={getValueByKey("SECURITY_MONITORING_THRESHOLD_BELOW_MARGE")}
                                                        onValueChange={(v) => updateRuleState("SECURITY_MONITORING_THRESHOLD_BELOW_MARGE", v)}
                                                        className="form-control input-sm"
                                                        decimalsLimit={2}
                                                        allowNegativeValue
                                                        placeholder="-1,0"
                                                    />
                                                </div>
                                            </td>
                                        </tr>

                                        <tr className="w-100">
                                            <td className="p-2 col-6">
                                                <div className="pp-form-cell-label">
                                                    <p className="m-0">Marge X%</p>
                                                </div>
                                            </td>
                                            <td className="p-2">
                                                <div className="input-group input-group-sm d-flex align-items-center">
                                                    <PPDecimalInput
                                                        value={getValueByKey("SECURITY_MONITORING_BELOW_MARGE_PERCENTAGE")}
                                                        onValueChange={(v) => updateRuleState("SECURITY_MONITORING_BELOW_MARGE_PERCENTAGE", v)}
                                                        id="input_marge_x"
                                                        className={`form-control`}
                                                        decimalsLimit={2}
                                                        suffix={"%"}
                                                        step={1}
                                                        maxLength={3}
                                                    />
                                                </div>
                                            </td>
                                        </tr>

                                        <tr className="w-100">
                                            <td className="p-2 col-6">
                                                <div className="pp-form-cell-label">
                                                    <p className="m-0">Anzahl Preisänderungen pr Stunde nach unten (Aktuell 0)</p>
                                                </div>
                                            </td>
                                            <td className="p-2">
                                                <div className="input-group input-group-sm d-flex align-items-center">
                                                    <input
                                                        value={getValueByKey("SECURITY_MONITORING_THRESHOLD_PRODUCTS_PRICE_REDUCED_COUNT")}
                                                        onChange={(e) => updateRuleState("SECURITY_MONITORING_THRESHOLD_PRODUCTS_PRICE_REDUCED_COUNT", e.target.value)}
                                                        type="number"
                                                        className="form-control input-sm"
                                                        id="input_Padj_per_h_down"
                                                        placeholder="-1.0"
                                                    />
                                                </div>
                                            </td>
                                        </tr>

                                        <tr className="w-100">
                                            <td className="p-2 col-6">
                                                <div className="pp-form-cell-label">
                                                    <p className="m-0">{`Anzahl Preisänderungen pro Stunde insgesamt: (Aktuell ${0})`}</p>
                                                </div>
                                            </td>
                                            <td className="p-2">
                                                <div className="input-group input-group-sm d-flex align-items-center">
                                                    <input
                                                        value={getValueByKey("SECURITY_MONITORING_THRESHOLD_COUNT_PRICE_CHANGES")}
                                                        onChange={(e) => updateRuleState("SECURITY_MONITORING_THRESHOLD_COUNT_PRICE_CHANGES", e.target.value)}
                                                        type="number"
                                                        className="form-control input-sm"
                                                        id="input_Padj_per_h_all"
                                                        placeholder="-1.0"
                                                    />
                                                </div>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            )}
                            </PPErrorBoundary>
                        </div>
                    </div>
                </div>
            </div>

            {/* Anbieter Filter Modal */}
            <Dialog
                header={editingProviderFilter ? "Anbieter Filter bearbeiten" : "Anbieter Filter"}
                footer={
                        <button
                            className="btn btn-primary btn-lg m-2"
                            onClick={async () => {
                                if (!producerFilterName.trim()) {
                                    toast.error("Bitte einen Filter-Namen eingeben");
                                    return;
                                }
                                if (!providerListText.trim()) {
                                    toast.error("Bitte mindestens einen Anbieter eintragen");
                                    return;
                                }

                                const providerFilter = {
                                    name: producerFilterName,
                                    filterData: serializeProviderFilterData({
                                        providerListText,
                                        blacklistWhitelistState,
                                        stockState,
                                        minRating,
                                    }),
                                    customerId: customerId
                                };

                                const response = editingProviderFilter
                                    ? await RuleEngineBackend.updateProviderFilter(token, editingProviderFilter.id, { ...editingProviderFilter, ...providerFilter })
                                    : await RuleEngineBackend.createProviderFilter(token, providerFilter);
                                if (response?.ok) {
                                    if (editingProviderFilter) {
                                        setProviderFilters((prev) =>
                                            prev.map((f) => (f.id === editingProviderFilter.id ? { ...f, ...providerFilter } : f))
                                        );
                                        toast.success("Anbieter-Filter aktualisiert");
                                    } else {
                                        const createdFilter = await response.json();
                                        setProviderFilters((prev) => [...prev, createdFilter]);
                                        toast.success("Anbieter-Filter erstellt");
                                    }
                                    setProducerFilterModalOpen(false);
                                    setEditingProviderFilter(null);
                                    resetProviderFilterForm();
                                } else {
                                    toast.error("Anbieter-Filter konnte nicht gespeichert werden");
                                }
                            }}
                        >{t("single.save")}</button>
                }
                visible={producerFilterModalOpen}
                onHide={() => {
                    setProducerFilterModalOpen(false);
                    setEditingProviderFilter(null);
                    resetProviderFilterForm();
                }}>
                <div className="p-4 d-flex flex-column" style={{ gap: "20px" }}>
                    <div>
                        <div className="w-100 row align-items-center">
                            <p className="col-md-auto mb-0">Filter-Name:</p>
                            <input
                                className="col-md-4 form-control"
                                value={producerFilterName}
                                onChange={(e) => setProducerFilterName(e.target.value)}
                                type="text"
                                placeholder="Filter-Name"
                            />
                        </div>
                    </div>

                    <div>
                        <h6 className="fw-semibold mb-2">Blacklist/Whitelist</h6>
                        <div>
                            <p>
                                {
                                    "Kommaseparierte Anbieter Liste (% = Platzhalter für beliebige Zeichenkette => Bsp.: 'Shop%' -> 'Shop123', 'ShopXY', ... ):"
                                }
                            </p>
                            <input
                                value={providerListText}
                                className="form-control w-50"
                                onChange={(e) => setProviderListText(e.target.value)}
                                placeholder="Shop A, Shop B, Shop%"
                                type="text"
                            />
                            <div className="ms-3 p-1 mt-3">
                                <label className="form-check form-switch">
<input
                                        className="form-check-input"
                                        type="checkbox"
                                        role="switch"
                                        checked={blacklistWhitelistState}
                                        onChange={() => setBlacklistWhitelistState(!blacklistWhitelistState)}
                                    />
<span className="form-check-label text-nowrap">
                                        {"Definierte Anbieter ausschließen (Blacklist)"}
                                    </span>
</label>
                                <label className="form-check form-switch">
<input
                                        className="form-check-input"
                                        type="checkbox"
                                        role="switch"
                                        checked={!blacklistWhitelistState}
                                        onChange={() => setBlacklistWhitelistState(!blacklistWhitelistState)}
                                    />
<span className="form-check-label text-nowrap">
                                        {"nur definierte Anbieter berücksichtigen (Whitelist)"}
                                    </span>
</label>
                            </div>
                        </div>
                    </div>

                    <div>
                        <h6 className="fw-semibold mb-2">Stock</h6>
                        <div className="d-flex flex-column ms-3 p-1">
                            <div className="d-flex flex-row">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    role="switch"
                                    id="provider-filter-stock-green"
                                    checked={stockState["green"]}
                                    onChange={() =>
                                        setStockState((prevState) => {
                                            prevState["green"] = !prevState["green"]
                                            return { ...prevState }
                                        })
                                    }
                                />
                                <label className="form-check-label text-nowrap" htmlFor="provider-filter-stock-green">Grün</label>
                            </div>

                            <div className="d-flex flex-row">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    role="switch"
                                    id="provider-filter-stock-yellow"
                                    checked={stockState["yellow"]}
                                    onChange={() =>
                                        setStockState((prevState) => {
                                            prevState["yellow"] = !prevState["yellow"]
                                            return { ...prevState }
                                        })
                                    }
                                />
                                <label className="form-check-label text-nowrap" htmlFor="provider-filter-stock-yellow">Gelb</label>
                            </div>

                            <div className="d-flex flex-row">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    role="switch"
                                    id="provider-filter-stock-red"
                                    checked={stockState["red"]}
                                    onChange={() =>
                                        setStockState((prevState) => {
                                            prevState["red"] = !prevState["red"]
                                            return { ...prevState }
                                        })
                                    }
                                />
                                <label className="form-check-label text-nowrap" htmlFor="provider-filter-stock-red">Rot</label>
                            </div>
                        </div>
                    </div>

                    <div>
                        <h6 className="fw-semibold mb-2">Bewertung</h6>
                        <div className="d-flex align-items-center flex-wrap" style={{ gap: "8px" }}>
                            <p className="text-lg mb-0">Anbieter haben mindestens</p>
                            <input
                                className="form-control"
                                style={{ width: "90px" }}
                                type="number"
                                value={minRating}
                                onChange={(e) => setMinRating(e.target.value)}
                            />
                            <p className="text-lg mb-0">Bewertungen.</p>
                        </div>
                    </div>
                </div>
            </Dialog>

            <PPDeleteConfirmationModal
                visible={backupRuleDeleteModalVisible}
                onClose={() => {
                    setBackupRuleDeleteModalVisible(false);
                    setBackupRulePendingDelete(null);
                }}
                onConfirm={confirmDeleteBackupRule}
                title="Backup-Regel löschen"
                message={
                    backupRulePendingDelete
                        ? `Sind Sie sicher, dass Sie die Backup-Regel "${backupRulePendingDelete.name || `ID ${backupRulePendingDelete.id}`}" löschen möchten?`
                        : "Sind Sie sicher, dass Sie diese Backup-Regel löschen möchten?"
                }
            />
        </>
    )
}
